// Import QueryClient at the top to avoid circular dependency issues
import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getAuthToken } from "./authToken";

// API base URL - uses environment variable in production, relative path in development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";


// Helper to construct full API URL - exported for use in other components
export function getApiUrl(path: string): string {
  // If path already starts with http, return as-is
  if (path.startsWith("http")) return path;
  
  // Ensure path starts with /
  let normalizedPath = path.startsWith("/") ? path : `/${path}`;
  
  // Normalize API_BASE_URL: remove trailing slashes and duplicate /api
  let normalizedBaseUrl = API_BASE_URL || "";
  if (normalizedBaseUrl.endsWith("/")) {
    normalizedBaseUrl = normalizedBaseUrl.slice(0, -1);
  }
  
  // Remove any double /api from base URL (e.g., /api/api -> /api)
  while (normalizedBaseUrl.endsWith("/api/api")) {
    normalizedBaseUrl = normalizedBaseUrl.slice(0, -4); // Remove "/api"
  }
  
  // IMPORTANT: Automatically add /api prefix if not present
  // Skip for portal routes, health checks, customer routes, and paths that already have /api
  // Note: /maps/ routes are API routes and need the /api prefix
  // This must happen BEFORE other normalization to ensure all API calls use /api prefix
  // Works for both empty base URL (relative paths) and full base URL (absolute paths)
  const shouldAddApiPrefix = 
    !normalizedPath.startsWith("/api/") && 
    !normalizedPath.startsWith("/portal/") && 
    !normalizedPath.startsWith("/health") && 
    !normalizedPath.startsWith("/customer/");
  
  if (shouldAddApiPrefix) {
    normalizedPath = `/api${normalizedPath}`;
  }
  
  // Prevent double /api in URL
  // If base URL ends with /api and path starts with /api, remove /api from path
  if (normalizedBaseUrl && normalizedBaseUrl.endsWith("/api") && normalizedPath.startsWith("/api")) {
    normalizedPath = normalizedPath.substring(4); // Remove "/api" prefix (4 characters)
    // Ensure it still starts with / after removing /api
    if (!normalizedPath.startsWith("/")) {
      normalizedPath = `/${normalizedPath}`;
    }
  }
  
  // Also check if normalizedPath already contains double /api and remove it
  normalizedPath = normalizedPath.replace(/\/api\/api/g, "/api");
  
  const fullUrl = `${normalizedBaseUrl}${normalizedPath}`;
  
  // Final check: remove any double /api from the full URL
  const finalUrl = fullUrl.replace(/\/api\/api/g, "/api");
  
  return finalUrl;
}

async function throwIfResNotOk(res: Response, requestUrl?: string) {
  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const isHtml = contentType.includes("text/html");
  
  // Check if we got HTML when expecting JSON (common when hitting wrong URL or redirect)
  if (res.ok && isHtml) {
    const text = await res.text();
    // Check if it looks like HTML (contains HTML tags)
    if (text.trim().startsWith("<!") || text.includes("<html") || text.includes("<!DOCTYPE")) {
      const url = res.url || "unknown";
      console.error("[API Error] Received HTML instead of JSON:", {
        url,
        status: res.status,
        contentType,
        preview: text.substring(0, 200),
      });
      throw new Error(
        `API returned HTML instead of JSON. This usually means:\n` +
        `1. The API URL is incorrect (check VITE_API_BASE_URL)\n` +
        `2. HTTP to HTTPS redirect is happening (check your server/proxy config)\n` +
        `3. Request is hitting frontend route instead of API route\n` +
        `URL: ${url}\n` +
        `Response preview: ${text.substring(0, 100)}...`
      );
    }
  }
  
  if (!res.ok) {
    // Handle 401 Unauthorized
    // Exclude authentication endpoints where 401 is a normal response (login, signup, etc.)
    if (res.status === 401) {
      // Check both the request URL and response URL to identify auth endpoints
      const url = requestUrl || res.url || "";
      const isAuthEndpoint = 
        url.includes("/api/auth/login") ||
        url.includes("/api/auth/signup") ||
        url.includes("/api/auth/register") ||
        url.includes("/api/auth/login/change-password") ||
        url.includes("/auth/login/change-password") ||
        url.includes("/customer/login") ||
        url.includes("/customer/password-reset") ||
        url.includes("/customer/magic-link") ||
        url.includes("/auth/login") ||
        url.includes("/auth/signup") ||
        url.includes("/auth/forgot-password") ||
        url.includes("/auth/reset-password") ||
        url.includes("/auth/change-password") ||
        url.includes("/password-reset") ||
        url.includes("/forgot-password") ||
        url.includes("/reset-password");
      
      // For auth endpoints, parse and throw the error as-is
      if (isAuthEndpoint) {
        // Parse the actual error message from the response
        if (isJson) {
          const errorData = await res.clone().json().catch(() => ({}));
          const error: any = new Error(errorData.message || errorData.error || "Invalid credentials");
          Object.assign(error, errorData);
          throw error;
        } else {
          const text = (await res.clone().text()) || res.statusText;
          throw new Error(text || "Invalid credentials");
        }
      }
      
      // For non-auth endpoints, throw 401 error (components will handle it)
      const error: any = new Error("Unauthorized");
      error.status = 401;
      throw error;
    }
    
    if (isJson) {
      const errorData = await res.json();
      const error: any = new Error(errorData.error || `${res.status}: ${res.statusText}`);
      Object.assign(error, errorData);
      error.status = res.status; // Ensure status is always included
      throw error;
    } else {
      const text = (await res.text()) || res.statusText;
      const error: any = new Error(`${res.status}: ${text}`);
      error.status = res.status; // Ensure status is always included
      throw error;
    }
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const fullUrl = getApiUrl(url);
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  
  if (data) {
    headers["Content-Type"] = "application/json";
  }
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res, url);
  
  // Double-check content type for successful responses
  const contentType = res.headers.get("content-type") || "";
  if (res.ok && !contentType.includes("application/json") && !contentType.includes("text/plain")) {
    const text = await res.text();
    console.error("[API Error] apiRequest got non-JSON response:", {
      method,
      url: fullUrl,
      contentType,
      status: res.status,
      preview: text.substring(0, 200),
    });
  }
  
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const fullUrl = getApiUrl(url);
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    const res = await fetch(fullUrl, {
      headers,
    });

    // Handle 401 based on behavior option
    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      // If "throw", let throwIfResNotOk handle it (which will show toast and redirect)
      // Don't return early here, let it go through throwIfResNotOk
    }

    await throwIfResNotOk(res, url);
    
    // Double-check content type before parsing JSON
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      const text = await res.text();
      console.error("[API Error] Expected JSON but got:", {
        contentType,
        url: fullUrl,
        preview: text.substring(0, 200),
      });
      throw new Error(
        `API returned ${contentType} instead of JSON. URL: ${fullUrl}`
      );
    }
    
    return await res.json();
  };

// Create QueryClient with error handling to catch constructor issues
let queryClient: QueryClient;
try {
  // Verify QueryClient is a constructor before instantiating
  // Handle both function and class cases (minification may change this)
  const QueryClientConstructor = QueryClient as any;
  
  if (typeof QueryClientConstructor !== 'function' && typeof QueryClientConstructor !== 'object') {
    throw new Error(`QueryClient is not a constructor. Type: ${typeof QueryClientConstructor}, Value: ${QueryClientConstructor}`);
  }
  
  // Try to instantiate - handle both direct call and new operator
  if (typeof QueryClientConstructor === 'function') {
    queryClient = new QueryClientConstructor({
      defaultOptions: {
        queries: {
          queryFn: getQueryFn({ on401: "throw" }),
          refetchInterval: false,
          refetchOnWindowFocus: true, // Refetch when user returns to tab
          refetchOnMount: true, // Always refetch on mount
          refetchOnReconnect: true, // Refetch when network reconnects
          staleTime: 0, // Data is immediately stale - always refetch
          gcTime: 0, // Don't keep unused data in cache (formerly cacheTime)
          retry: false,
          // Error handling is done in queryFn - onError is deprecated in v5
        },
        mutations: {
          retry: false,
          // Error handling is done in mutation functions - onError is deprecated in v5
        },
      },
    });
  } else {
    // Fallback if QueryClient is exported differently
    throw new Error('QueryClient is not callable as a constructor');
  }
} catch (error) {
  console.error('Failed to create QueryClient:', error);
  console.error('QueryClient type:', typeof QueryClient);
  console.error('QueryClient:', QueryClient);
  
  // Fallback: try to create a minimal QueryClient
  try {
    const QueryClientConstructor = QueryClient as any;
    queryClient = new QueryClientConstructor({
      defaultOptions: {
        queries: {
          staleTime: 0,
          gcTime: 0,
          refetchOnMount: true,
          refetchOnWindowFocus: true,
          retry: false,
        },
        mutations: {
          retry: false,
        },
      },
    });
  } catch (fallbackError) {
    console.error('Fallback QueryClient creation also failed:', fallbackError);
    // Last resort: create a mock object (this will cause runtime errors but won't crash)
    queryClient = {} as QueryClient;
  }
}

// Clear any existing React Query cache from localStorage on startup
// This ensures no stale cached data is loaded
if (typeof window !== "undefined") {
  try {
    localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
  } catch (error) {
    console.error("Failed to clear existing cache:", error);
  }
}

/**
 * Clear React Query cache from memory
 * Note: Caching is disabled, but this can still clear in-memory data
 */
export function clearReactQueryCache() {
  // Clear in-memory cache
  queryClient.clear();
}

/**
 * Clear specific query cache (e.g., jobs)
 * Note: Caching is disabled, but this can still clear in-memory data
 */
export function clearQueryCache(queryKey: string[]) {
  queryClient.removeQueries({ queryKey });
}

export { queryClient };
